"use client";

import { useState, useEffect, useContext } from "react";
import {
  ChevronLeft,
  ChevronDown,
  Wallet,
  ShoppingCart,
  ChevronUp,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import BetSummaryPopup from "../../components/Popup/BetSummaryPopup";
import WalletPopup from "../../components/Popup/WalletPopup";
import { useWebSocket } from "../../context/WebSocketContext";
import { gamesApi } from "../../api/games";
import { NavigationContext } from "../../context/NavigationContext";
import { motion } from "framer-motion";

export default function KeypadHeader({
  balance,
  selectedGameName = { name: "" },
  betData,
  betTotal,
  handleSubmit,
  showSummary,
  setShowSummary,
  onRemoveBet,
  clearAllBets,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedGame, setSelectedGame] = useState("");
  const [localBalance, setLocalBalance] = useState(balance);
  const { walletBalance } = useWebSocket();
  
  const USER_GAMES = JSON.parse(sessionStorage.getItem("user_games"))
    ? JSON.parse(sessionStorage.getItem("user_games"))
    : [];
  const cartCount = betData.length;
  
  // Calculate the number of unique games in the cart
  const gameCount = betData.length > 0 
    ? new Set(betData.map(bet => bet.gameId)).size 
    : 0;
  
  // Determine if we have a multi-game cart
  const hasMultipleGames = gameCount > 1;

  // Use WebSocket balance if available, fallback to prop
  useEffect(() => {
    if (walletBalance) {
      setLocalBalance(walletBalance);
    } else if (balance) {
      setLocalBalance(balance);
    }
  }, [walletBalance, balance]);

  // Listen for wallet refresh events
  useEffect(() => {
    const handleWalletRefresh = async () => {
      // The wallet will be refreshed through WebSocket context
    };

    window.addEventListener('walletRefresh', handleWalletRefresh);
    
    return () => {
      window.removeEventListener('walletRefresh', handleWalletRefresh);
    };
  }, []);

  // Force update when betData changes
  useEffect(() => {
    // This empty effect ensures the component re-renders when betData changes
  }, [betData, betTotal]);

  useEffect(() => {
    if (selectedGameName) {
      setSelectedGame(selectedGameName);
    }
  }, [selectedGameName]);

  const { handleNavigate } = useContext(NavigationContext);
  const navigate = useNavigate();

  // Generate a formatted date for today
  const formattedDate = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });

  return (
    <>
      <header className="flex items-center justify-between pr-3 pt-4 pb-5">
        <div className="flex items-center gap-2 relative">
          <motion.button
            className="text-white"
            onClick={() => handleNavigate(address, true, navigate)} // Use handleNavigate with back navigation
            whileHover={{ scale: 1.1 }} // Slightly enlarge on hover
            whileTap={{ scale: 0.9 }} // Shrink on click
            initial={{ x: 0 }} // Start at the default position
            animate={{ x: [0, -5, 0] }} // Slight bounce effect when rendered
            transition={{ duration: 0.3 }} // Timing for the bounce
          >
            <ChevronLeft className="h-6 w-6" onClick={() => handleNavigate('/home', true, navigate)} />
          </motion.button>
          <button
            id="dropdownButton"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1 text-white pl-2"
          >
            <span className="font-medium text-sm">
              {selectedGame ||
                (USER_GAMES.length > 0 ? USER_GAMES[0].name : "")}
            </span>
            {dropdownOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}{" "}
          </button>

          {dropdownOpen && (
            <ul className="absolute top-5 left-2 z-10 mt-1 bg-white border rounded-md shadow-md w-56">
              {USER_GAMES.map((game, index) => (
                <li
                  key={game.id}
                  onClick={() => {
                    window.location.href = `/bets/${game.id}`;
                  }}
                  className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                    index !== 0 ? "border-t border-[#E1E7EF]" : ""
                  }`}
                >
                  {game.name.toUpperCase()}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            className="flex items-center justify-between gap-1 text-white px-1 py-0.5 rounded-lg text-sm font-semibold border"
            onClick={() => setShowPopup(true)}
          >
            <Wallet className="h-4 w-4" />
            <span className="ps-1">{Intl.NumberFormat('en-US', { style: 'currency', currency: 'PHP' }).format(localBalance)}</span>
          </button>
          
          <div 
            className="relative flex items-center" 
            onClick={() => setShowSummary(true)}
          >
            <ShoppingCart className="h-6 w-6 text-white" />
            {cartCount > 0 && (
              <>
                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
                {/* {hasMultipleGames && (
                  <span className="text-white text-xs ml-1">
                    ₱{betTotal.toFixed(2)} ({gameCount} games)
                  </span>
                )} */}
              </>
            )}
          </div>
        </div>
      </header>

      <BetSummaryPopup
        isOpen={showSummary}
        onClose={() => setShowSummary(false)}
        drawTime="2:00 PM"
        drawDate={formattedDate}
        betData={betData}
        betTotal={betTotal}
        onRemoveBet={onRemoveBet}
        onSubmit={handleSubmit}
        clearAllBets={clearAllBets}
        showAllBets={true}
      />

      <WalletPopup
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        currentBalance={localBalance || 0}
      />
    </>
  );
}
